import { createSlice } from "@reduxjs/toolkit";
import {
  getNotificationList,
  notificationReading,
  getNotificationListUnread,
  incrementUnreadQuantity,
  fetchRecipients,
  fetchUnreadCount,
  markRecipientsRead,
  markRecipientsUnread,
  deleteRecipients,
  markLegacyNotificationRead,
  markRecipientsReadByDelivery,
} from "../actions";

const initialState = {
  activeNotifications: null,
  notificationsModal: {
    open: false,
  },
  // --- Legacy (centro-notifiche) ---
  getNotificationList: {
    data: null,
    isLoading: false,
    error: false,
    extra: null,
  },
  getNotificationListUnread: {
    quantity: "",
    data: null,
    isLoading: false,
    error: false,
  },
  notificationReading: {
    isLoading: false,
    error: false,
  },
  // --- v1 Recipients API ---
  recipients: {
    items: [],
    meta: {},
    isLoading: false,
    error: null,
  },
  recipientsUnreadCount: 0,
  badgeManuallyReset: false, // When true, fetchUnreadCount won't override the badge count
  // --- Popup state ---
  pendingPopup: null, // full recipient object to show as popup
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    resetGetNotificationListError: (state) => {
      state.getNotificationList.error = false;
    },
    resetNotificationReadingError: (state) => {
      state.notificationReading.error = false;
    },
    updateActiveNotifications: (state, payload) => {
      state.activeNotifications = payload.payload;
    },
    updateNotificationsModal: (state, payload) => {
      state.notificationsModal.open = payload.payload;
    },
    showPopup: (state, action) => {
      state.pendingPopup = action.payload;
    },
    dismissPopup: (state) => {
      state.pendingPopup = null;
    },
    incrementRecipientsUnreadCount: (state) => {
      state.recipientsUnreadCount += 1;
      // New push arrived — allow fetchUnreadCount to update badge again
      state.badgeManuallyReset = false;
    },
    resetRecipientsUnreadCount: (state) => {
      state.recipientsUnreadCount = 0;
      // Prevent fetchUnreadCount from overriding until a new push arrives
      state.badgeManuallyReset = true;
    },
    allowBadgeUpdate: (state) => {
      state.badgeManuallyReset = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getNotificationList.pending, (state) => {
      state.getNotificationList.isLoading = true;
      state.getNotificationList.error = false;
      state.getNotificationListUnread.quantity = 0;
    });
    builder.addCase(getNotificationList.fulfilled, (state, action) => {
      state.getNotificationList.isLoading = false;
      state.getNotificationList.error = false;
      state.getNotificationList.data =
        state.getNotificationList.data &&
        action.payload.notifications.current_page > 1
          ? [
              ...state.getNotificationList.data,
              ...action.payload.notifications.data,
            ]
          : action.payload.notifications.data;
      state.getNotificationList.extra = action.payload;

      const unreadFromLastOpen =
        action.payload?.data?.unread_from_last_open ?? 0;
      state.getNotificationListUnread.quantity = unreadFromLastOpen;
    });
    builder.addCase(getNotificationList.rejected, (state, action) => {
      state.getNotificationList.isLoading = false;
      state.getNotificationList.error = action.error.message;
    });

    builder.addCase(getNotificationListUnread.pending, (state) => {
      state.getNotificationListUnread.isLoading = true;
      state.getNotificationListUnread.error = false;
    });
    builder.addCase(getNotificationListUnread.fulfilled, (state, action) => {
      state.getNotificationListUnread.isLoading = false;
      state.getNotificationListUnread.error = false;
      const unreadFromLastOpen =
        action.payload.data?.unread_from_last_open ?? 0;
      state.getNotificationListUnread.quantity = unreadFromLastOpen;
      state.getNotificationListUnread.data =
        action.payload.notifications?.data || [];
    });
    builder.addCase(getNotificationListUnread.rejected, (state, action) => {
      state.getNotificationListUnread.isLoading = false;
      state.getNotificationListUnread.error = action.error.message;
    });

    builder.addCase(incrementUnreadQuantity.fulfilled, (state, action) => {
      state.getNotificationListUnread.quantity = action.payload;
    });

    builder.addCase(notificationReading.pending, (state, action) => {
      state.notificationReading.isLoading = true;
      state.notificationReading.error = false;

      const wasAlreadyRead = !!action.meta?.arg?.is_read_by_current_user;
      if (!wasAlreadyRead) {
        const currentQty =
          Number(state.getNotificationListUnread.quantity) || 0;
        state.getNotificationListUnread.quantity = Math.max(
          currentQty - 1,
          0,
        );
        state.getNotificationListUnread.data =
          state.getNotificationListUnread.data.filter(
            (n) => n.id !== action.meta.arg.id,
          );
      }
    });
    builder.addCase(notificationReading.fulfilled, (state, action) => {
      state.notificationReading.isLoading = false;
      state.notificationReading.error = false;

      const unreadFromLastOpen =
        action.payload?.data?.unread_from_last_open ?? 0;
      state.getNotificationListUnread.quantity = unreadFromLastOpen;
    });
    builder.addCase(notificationReading.rejected, (state, action) => {
      state.notificationReading.isLoading = false;
      state.notificationReading.error = action.error.message;

      const wasAlreadyRead = !!action.meta?.arg?.is_read_by_current_user;
      if (!wasAlreadyRead) {
        state.getNotificationListUnread.quantity++;
        state.getNotificationListUnread.data.push(action.meta.arg);
      }
    });

    // ─── v1 Recipients API ───

    // fetchRecipients
    builder.addCase(fetchRecipients.pending, (state) => {
      state.recipients.isLoading = true;
      state.recipients.error = null;
    });
    builder.addCase(fetchRecipients.fulfilled, (state, action) => {
      state.recipients.isLoading = false;
      state.recipients.error = null;
      const { data, meta, page } = action.payload;
      if (page > 1) {
        // Append for pagination
        state.recipients.items = [...state.recipients.items, ...data];
      } else {
        state.recipients.items = data;
      }
      state.recipients.meta = meta;
    });
    builder.addCase(fetchRecipients.rejected, (state, action) => {
      state.recipients.isLoading = false;
      state.recipients.error = action.payload || action.error?.message;
    });

    // fetchUnreadCount — only update badge if not manually reset
    // (prevents AppStateManager/resume from overriding the reset after visiting notification center)
    builder.addCase(fetchUnreadCount.fulfilled, (state, action) => {
      if (!state.badgeManuallyReset) {
        state.recipientsUnreadCount = action.payload.total;
      }
    });

    // markRecipientsRead — optimistic
    builder.addCase(markRecipientsRead.pending, (state, action) => {
      const ids = action.meta.arg;
      state.recipients.items = state.recipients.items.map((item) =>
        ids.includes(item.id)
          ? {
              ...item,
              attributes: {
                ...item.attributes,
                read_at: new Date().toISOString(),
              },
            }
          : item
      );
      state.recipientsUnreadCount = Math.max(
        state.recipientsUnreadCount - ids.length,
        0
      );
    });
    builder.addCase(markRecipientsRead.rejected, (state, action) => {
      // Revert optimistic read
      const ids = action.meta.arg;
      state.recipients.items = state.recipients.items.map((item) =>
        ids.includes(item.id)
          ? {
              ...item,
              attributes: { ...item.attributes, read_at: null },
            }
          : item
      );
      state.recipientsUnreadCount += ids.length;
    });

    // markRecipientsUnread — optimistic
    builder.addCase(markRecipientsUnread.pending, (state, action) => {
      const ids = action.meta.arg;
      state.recipients.items = state.recipients.items.map((item) =>
        ids.includes(item.id)
          ? {
              ...item,
              attributes: { ...item.attributes, read_at: null },
            }
          : item
      );
      state.recipientsUnreadCount += ids.length;
    });

    // deleteRecipients — optimistic
    builder.addCase(deleteRecipients.pending, (state, action) => {
      const ids = action.meta.arg;
      const removed = state.recipients.items.filter((item) =>
        ids.includes(item.id)
      );
      const unreadRemoved = removed.filter(
        (item) => !item.attributes?.read_at
      ).length;
      state.recipients.items = state.recipients.items.filter(
        (item) => !ids.includes(item.id)
      );
      state.recipientsUnreadCount = Math.max(
        state.recipientsUnreadCount - unreadRemoved,
        0
      );
    });

    // markLegacyNotificationRead — optimistic on pending
    // Match by both item.id and item.attributes.notification_id since
    // the arg may be notification_id (from feed click) or notification id (from legacy list)
    builder.addCase(markLegacyNotificationRead.pending, (state, action) => {
      const notifId = action.meta.arg;
      state.recipients.items = state.recipients.items.map((item) =>
        (item.id === notifId || item?.attributes?.notification_id === notifId)
          ? {
              ...item,
              attributes: {
                ...item.attributes,
                read_at: new Date().toISOString(),
                is_read_by_current_user: true,
              },
            }
          : item
      );
      state.recipientsUnreadCount = Math.max(state.recipientsUnreadCount - 1, 0);
    });
    builder.addCase(markLegacyNotificationRead.rejected, (state, action) => {
      // Revert
      const notifId = action.meta.arg;
      state.recipients.items = state.recipients.items.map((item) =>
        (item.id === notifId || item?.attributes?.notification_id === notifId)
          ? {
              ...item,
              attributes: {
                ...item.attributes,
                read_at: null,
                is_read_by_current_user: false,
              },
            }
          : item
      );
      state.recipientsUnreadCount += 1;
    });

    // markRecipientsReadByDelivery — push-originated, just decrement count
    builder.addCase(markRecipientsReadByDelivery.fulfilled, (state) => {
      state.recipientsUnreadCount = Math.max(state.recipientsUnreadCount - 1, 0);
    });
  },
});

export const {
  resetGetNotificationListError,
  resetNotificationReadingError,
  updateActiveNotifications,
  updateNotificationsModal,
  showPopup,
  dismissPopup,
  incrementRecipientsUnreadCount,
  resetRecipientsUnreadCount,
  allowBadgeUpdate,
} = notificationsSlice.actions;
export default notificationsSlice.reducer;
